import { validateModbusHost } from "./network/modbus-device-model";

export const MODBUS_TABLES = Object.freeze([
  { value: "coils", label: "Coils", writable: true },
  { value: "discrete_inputs", label: "Discrete inputs", writable: false },
  { value: "holding_registers", label: "Holding registers", writable: true },
  { value: "input_registers", label: "Input registers", writable: false },
]);

export const MODBUS_REGISTER_TYPES = Object.freeze([
  { value: "uint16", label: "Unsigned 16-bit", width: 1 },
  { value: "int16", label: "Signed 16-bit", width: 1 },
  { value: "bcd16", label: "BCD 16-bit", width: 1 },
  { value: "uint32", label: "Unsigned 32-bit", width: 2 },
  { value: "int32", label: "Signed 32-bit", width: 2 },
  { value: "bcd32", label: "BCD 32-bit", width: 2 },
  { value: "uint64", label: "Unsigned 64-bit", width: 4 },
  { value: "int64", label: "Signed 64-bit", width: 4 },
  { value: "float32", label: "Float 32-bit", width: 2 },
  { value: "float64", label: "Float 64-bit", width: 4 },
  { value: "string", label: "Fixed-length string", width: 0 },
]);

const TABLE_NAMES = new Set(MODBUS_TABLES.map((item) => item.value));
const REGISTER_WIDTHS = new Map(
  MODBUS_REGISTER_TYPES.map((item) => [item.value, item.width])
);
const BIT_TABLES = new Set(["coils", "discrete_inputs"]);
let nextMappingKey = 1;

// Go's zero time serializes as year 0001. JavaScript accepts that as a valid
// Date, but it is lifecycle sentinel data rather than evidence that a server
// started. Keep every protocol-server surface consistent and truthful.
export function formatProtocolServerDateTime(value) {
  if (!value) return "Not started";
  const date = new Date(value);
  if (Number.isNaN(date.getTime()) || date.getUTCFullYear() <= 1) {
    return "Not started";
  }
  return date.toLocaleString();
}

const integer = (value) => {
  const text = String(value ?? "").trim();
  return text === "" ? Number.NaN : Number(text);
};

const requireInteger = (value, label, min, max) => {
  const parsed = integer(value);
  if (!Number.isInteger(parsed) || parsed < min || parsed > max) {
    return `${label} must be a whole number from ${min} to ${max}.`;
  }
  return "";
};

const appendError = (current, next) => (current ? `${current} ${next}` : next);

export function splitListenAddress(value) {
  const input = String(value || "").trim();
  const bracketed = input.match(/^\[([^\]]+)]:(\d+)$/);
  if (bracketed) return { host: bracketed[1], port: bracketed[2] };
  const separator = input.lastIndexOf(":");
  if (separator <= 0) return { host: input || "127.0.0.1", port: "1502" };
  return {
    host: input.slice(0, separator),
    port: input.slice(separator + 1),
  };
}

export function joinListenAddress(host, port) {
  const cleanHost = String(host || "").trim();
  const formattedHost = cleanHost.includes(":") ? `[${cleanHost}]` : cleanHost;
  return `${formattedHost}:${String(port ?? "").trim()}`;
}

export function validateListenHost(value) {
  const host = String(value || "").trim();
  if (!host) return "Listen IP address is required.";
  if (host === "localhost") return "";
  if (host.includes("%")) {
    return "Listen IP address cannot include an IPv6 interface zone.";
  }
  if (!host.includes(":") && !/^\d+(?:\.\d+){3}$/.test(host)) {
    return "Listen address must be an explicit IPv4 or IPv6 address, or localhost.";
  }
  const error = validateModbusHost(host);
  return error ? `Listen IP address is invalid. ${error}` : "";
}

export function mappingWidth(mapping) {
  if (BIT_TABLES.has(mapping?.table)) return 1;
  if (mapping?.valueType === "string") {
    const length = integer(mapping?.stringLength);
    return Number.isInteger(length) && length >= 2 && length <= 250 && length % 2 === 0
      ? length / 2
      : 0;
  }
  return REGISTER_WIDTHS.get(mapping?.valueType) || 0;
}

export function mappingEndAddress(mapping) {
  const start = integer(mapping?.address);
  const width = mappingWidth(mapping);
  return Number.isInteger(start) && width > 0 ? start + width - 1 : Number.NaN;
}

export function createMapping(overrides = {}) {
  const table = TABLE_NAMES.has(overrides.table) ? overrides.table : "coils";
  return {
    key: overrides.key || `modbus-map-${nextMappingKey++}`,
    table,
    address: String(overrides.address ?? 0),
    tagPath: String(overrides.tagPath ?? ""),
    valueType: BIT_TABLES.has(table)
      ? "bool"
      : (REGISTER_WIDTHS.has(overrides.valueType) ? overrides.valueType : "uint16"),
    byteOrder: overrides.byteOrder === "little" ? "little" : "big",
    wordOrder: overrides.wordOrder === "little" ? "little" : "big",
    stringLength: String(overrides.stringLength ?? 16),
    stringRaw: overrides.stringRaw === true,
    stringRightJustify: overrides.stringRightJustify === true,
  };
}

export function protocolServerFormFromConfig(config = {}) {
  const listener = splitListenAddress(config.listen_address || "127.0.0.1:1502");
  return {
    enabled: config.enabled === true,
    listenHost: listener.host,
    listenPort: listener.port,
    unitId: String(config.unit_id ?? 1),
    maxClients: String(config.max_clients ?? 16),
    requestTimeoutMs: String(config.request_timeout_ms ?? 5000),
    idleTimeoutMs: String(config.idle_timeout_ms ?? 300000),
    sourceCidrsText: (Array.isArray(config.source_cidrs) ? config.source_cidrs : [])
      .map((cidr) => String(cidr || "").trim())
      .filter(Boolean)
      .join("\n"),
    mappings: (Array.isArray(config.mappings) ? config.mappings : []).map((mapping) =>
      createMapping({
        table: mapping.table,
        address: mapping.address,
        tagPath: mapping.tag_path,
        valueType: mapping.value_type,
        byteOrder: mapping.byte_order,
        wordOrder: mapping.word_order,
        stringLength: mapping.string_length,
        stringRaw: mapping.string_raw,
        stringRightJustify: mapping.string_right_justify,
      })
    ),
  };
}

// parseSourceCidrList splits the free-text CIDR editor (one entry per line or
// comma-separated) into a trimmed, de-duplicated list preserving order.
export function parseSourceCidrList(text) {
  const seen = new Set();
  const result = [];
  for (const raw of String(text || "").split(/[\n,]/)) {
    const cidr = raw.trim();
    if (!cidr || seen.has(cidr)) continue;
    seen.add(cidr);
    result.push(cidr);
  }
  return result;
}

export function buildProtocolServerPayload(form) {
  return {
    enabled: form.enabled === true,
    listen_address: joinListenAddress(form.listenHost, form.listenPort),
    unit_id: integer(form.unitId),
    max_clients: integer(form.maxClients),
    request_timeout_ms: integer(form.requestTimeoutMs),
    idle_timeout_ms: integer(form.idleTimeoutMs),
    source_cidrs: parseSourceCidrList(form.sourceCidrsText),
    mappings: (form.mappings || []).map((mapping) => {
      const result = {
        table: mapping.table,
        address: integer(mapping.address),
        tag_path: String(mapping.tagPath || "").trim(),
      };
      if (!BIT_TABLES.has(mapping.table)) {
        result.value_type = mapping.valueType;
        result.byte_order = mapping.byteOrder;
        result.word_order = mapping.wordOrder;
        if (mapping.valueType === "string") {
          result.string_length = integer(mapping.stringLength);
          result.string_raw = mapping.stringRaw === true;
          result.string_right_justify = mapping.stringRightJustify === true;
        }
      }
      return result;
    }),
  };
}

export function validateProtocolServerForm(form) {
  const formErrors = {
    listenHost: validateListenHost(form?.listenHost),
    listenPort: requireInteger(form?.listenPort, "Listen port", 1, 65535),
    unitId: requireInteger(form?.unitId, "Unit ID", 0, 255),
    maxClients: requireInteger(form?.maxClients, "Maximum clients", 1, 1024),
    requestTimeoutMs: requireInteger(
      form?.requestTimeoutMs,
      "Request timeout",
      100,
      60000
    ),
    idleTimeoutMs: requireInteger(
      form?.idleTimeoutMs,
      "Idle client timeout",
      1000,
      3600000
    ),
    sourceCidrs: validateSourceCidrs(form),
  };
  const mappings = Array.isArray(form?.mappings) ? form.mappings : [];
  const mappingErrors = mappings.map((mapping) => {
    let error = "";
    if (!TABLE_NAMES.has(mapping?.table)) {
      error = appendError(error, "Select a supported Modbus table.");
    }
    const addressError = requireInteger(mapping?.address, "Address", 0, 65535);
    if (addressError) error = appendError(error, addressError);
    if (!String(mapping?.tagPath || "").trim()) {
      error = appendError(error, "Tag path is required.");
    }
    if (!BIT_TABLES.has(mapping?.table)) {
      if (!REGISTER_WIDTHS.has(mapping?.valueType)) {
        error = appendError(error, "Select a supported register value type.");
      }
      if (!new Set(["big", "little"]).has(mapping?.byteOrder)) {
        error = appendError(error, "Select a byte order.");
      }
      if (!new Set(["big", "little"]).has(mapping?.wordOrder)) {
        error = appendError(error, "Select a word order.");
      }
      if (mapping?.valueType === "string") {
        const stringLengthError = requireInteger(mapping?.stringLength, "String length", 2, 250);
        if (stringLengthError || integer(mapping?.stringLength) % 2 !== 0) {
          error = appendError(error, "String length must be an even number from 2 to 250 bytes.");
        }
        if (mapping?.wordOrder !== "big") {
          error = appendError(error, "Fixed-length strings require high word first.");
        }
      }
    }
    const end = mappingEndAddress(mapping);
    if (Number.isInteger(end) && end > 65535) {
      error = appendError(
        error,
        `The ${mappingWidth(mapping)}-register value extends past address 65535.`
      );
    }
    return error;
  });

  for (let left = 0; left < mappings.length; left += 1) {
    const leftStart = integer(mappings[left]?.address);
    const leftEnd = mappingEndAddress(mappings[left]);
    if (!Number.isInteger(leftStart) || !Number.isInteger(leftEnd)) continue;
    for (let right = left + 1; right < mappings.length; right += 1) {
      if (mappings[left]?.table !== mappings[right]?.table) continue;
      const rightStart = integer(mappings[right]?.address);
      const rightEnd = mappingEndAddress(mappings[right]);
      if (!Number.isInteger(rightStart) || !Number.isInteger(rightEnd)) continue;
      if (leftStart <= rightEnd && rightStart <= leftEnd) {
        const message = `Address range ${Math.max(leftStart, rightStart)}–${Math.min(
          leftEnd,
          rightEnd
        )} overlaps another ${mappings[left].table.replaceAll("_", " ")} mapping.`;
        mappingErrors[left] = appendError(mappingErrors[left], message);
        mappingErrors[right] = appendError(mappingErrors[right], message);
      }
    }
  }

  const firstFormError = Object.values(formErrors).find(Boolean) || "";
  const firstMappingError = mappingErrors.find(Boolean) || "";
  return {
    valid: !firstFormError && !firstMappingError,
    message: firstFormError || firstMappingError,
    formErrors,
    mappingErrors,
  };
}

export function normalizeModbusServerStatus(status = {}) {
  const metrics = status.metrics || {};
  return {
    available: status.available === true,
    enabled: status.enabled === true,
    running: status.running === true,
    listenAddress: String(status.listen_address || ""),
    unitId: Number(status.unit_id ?? 0),
    mappingCount: Number(status.mapping_count ?? 0),
    startedAt: status.started_at || "",
    lastError: String(status.last_error || ""),
    metrics: {
      activeClients: Number(metrics.active_clients ?? 0),
      acceptedClients: Number(metrics.accepted_clients ?? 0),
      rejectedClients: Number(metrics.rejected_clients ?? 0),
      requests: Number(metrics.requests ?? 0),
      exceptions: Number(metrics.exceptions ?? 0),
      transportErrors: Number(metrics.transport_errors ?? 0),
    },
  };
}

export function isWildcardListenHost(host) {
  const value = String(host || "").trim();
  return value === "0.0.0.0" || value === "::";
}

// Mirrors the backend listenHostIsLoopback: an empty source allowlist is only
// accepted for loopback listeners.
export function listenHostIsLoopback(host) {
  const value = String(host || "").trim().toLowerCase();
  if (value === "localhost" || value === "::1") return true;
  return /^127\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(value);
}

const MAX_SOURCE_CIDRS = 64;

function isCidr(entry) {
  const match = String(entry).match(/^(.+)\/(\d{1,3})$/);
  if (!match) return false;
  const host = match[1];
  const prefix = Number(match[2]);
  if (host.includes(":")) {
    // IPv6: structural check only; the backend performs the authoritative parse.
    return /^[0-9a-fA-F:]+$/.test(host) && prefix >= 0 && prefix <= 128;
  }
  const octets = host.split(".");
  if (octets.length !== 4) return false;
  if (!octets.every((octet) => /^\d{1,3}$/.test(octet) && Number(octet) <= 255)) {
    return false;
  }
  return prefix >= 0 && prefix <= 32;
}

// validateSourceCidrs enforces the same policy as the backend: valid CIDRs,
// at most MAX_SOURCE_CIDRS, and a non-empty allowlist for an enabled
// non-loopback listener (Modbus TCP has no authentication of its own).
function validateSourceCidrs(form) {
  const entries = parseSourceCidrList(form?.sourceCidrsText);
  if (entries.length > MAX_SOURCE_CIDRS) {
    return `At most ${MAX_SOURCE_CIDRS} source CIDRs are allowed.`;
  }
  const invalid = entries.find((entry) => !isCidr(entry));
  if (invalid) {
    return `"${invalid}" is not a valid CIDR (for example 10.0.0.0/24 or 192.168.1.5/32).`;
  }
  if (
    form?.enabled === true &&
    !listenHostIsLoopback(form?.listenHost) &&
    entries.length === 0
  ) {
    return "Enabled non-loopback listeners require at least one source CIDR. Modbus TCP has no authentication, so restrict which hosts may connect.";
  }
  return "";
}
